import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { keyframes } from 'styled-components';
import { hapticsImpactLight } from '../../common/haptic';

type Size = 'sm' | 'md' | 'lg';

export type VerificationCodeInputRef = {
  focus: (index?: number) => void;
  clear: () => void;
  setCode: (code: string) => void;
  value: string;
};

type Props = {
  length?: number; // default 6
  onChange?: (code: string, values: string[]) => void;
  onComplete?: (code: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: boolean;
  size?: Size; // sm | md | lg
  className?: string;
  name?: string; // base name for inputs (optional)
  ariaLabel?: string;
  mask?: boolean; // shows • instead of the digit

  // NEW
  loading?: boolean; // when true: block onComplete, disable, show overlay
  loadingText?: string; // optional label under spinner
};

const VerificationCodeInput = forwardRef<VerificationCodeInputRef, Props>(
  function VerificationCodeInput(
    {
      length = 6,
      onChange,
      onComplete,
      autoFocus = true,
      disabled = false,
      error = false,
      size = 'md',
      className,
      name = 'deks-code',
      ariaLabel = 'Verification code',
      mask = false,
      loading = false,
      loadingText = 'Verifying…',
    },
    ref,
  ) {
    const [values, setValues] = useState<string[]>(() =>
      Array.from({ length }, () => ''),
    );
    const inputs = useRef<Array<HTMLInputElement | null>>([]);
    const lastCompletedRef = useRef<string>(''); // dedupe onComplete

    // keep refs array stable length
    inputs.current = useMemo(
      () => Array.from({ length }, (_, i) => inputs.current[i] ?? null),
      [length],
    );

    const code = useMemo(() => values.join(''), [values]);
    const isDisabled = disabled || loading;

    useImperativeHandle(
      ref,
      () => ({
        focus: (index = 0) => {
          inputs.current[index]?.focus();
        },
        clear: () => {
          setValues(Array.from({ length }, () => ''));
          inputs.current[0]?.focus();
          lastCompletedRef.current = '';
        },
        setCode: (next: string) => {
          const onlyDigits = (next || '').replace(/\D/g, '').slice(0, length);
          const nextVals = onlyDigits.padEnd(length, '').split('');
          setValues(nextVals);
        },
        get value() {
          return code;
        },
      }),
      [code, length],
    );

    // Fire onChange every time; fire onComplete only when not loading and not deduped
    useEffect(() => {
      onChange?.(code, values);
      const allFilled = values.every(Boolean) && values.length === length;
      if (!loading && allFilled && code !== lastCompletedRef.current) {
        lastCompletedRef.current = code;
        onComplete?.(code);
      }
    }, [code, values, length, loading, onChange, onComplete]);

    useEffect(() => {
      const allFilled = values.every(Boolean);
      if (allFilled && code !== lastCompletedRef.current && !loading) {
        lastCompletedRef.current = code;
        onComplete?.(code);
      }
    }, [values, code, loading, onComplete]);

    const focusPrev = (i: number) => {
      if (i > 0) inputs.current[i - 1]?.focus();
    };
    const focusNext = (i: number) => {
      if (i < length - 1) inputs.current[i + 1]?.focus();
    };

    const setAt = (i: number, ch: string) => {
      setValues((prev) => {
        const next = prev.slice();
        next[i] = ch;
        return next;
      });
    };

    const handleChange = (i: number, raw: string) => {
      if (isDisabled) return;
      const digits = (raw || '').replace(/\D/g, '');

      // Handle iOS autofill of full code (e.g., "123456")
      if (digits.length > 1) {
        const slice = digits.slice(0, length - i).split('');
        setValues((prev) => {
          const next = prev.slice();
          for (let j = 0; j < slice.length; j++) next[i + j] = slice[j];
          return next;
        });

        // Focus the last filled input or blur if complete
        const lastFilledIndex = Math.min(i + slice.length - 1, length - 1);
        if (lastFilledIndex < length - 1) {
          inputs.current[lastFilledIndex + 1]?.focus();
        } else {
          inputs.current[lastFilledIndex]?.blur();
        }
        return;
      }

      // Regular single-digit behavior
      const digit = digits.slice(0, 1);
      if (!digit) return;
      setAt(i, digit);
      if (i === length - 1) {
        inputs.current[i]?.blur();
      } else {
        focusNext(i);
      }
    };

    // helper
    const isDigitKey = (e: React.KeyboardEvent<HTMLInputElement>) =>
      e.key.length === 1 && /\d/.test(e.key);

    const handleKeyDown = (
      i: number,
      e: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (isDisabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      const key = e.key;

      // 1) DIGITS: take over typing so we advance even if same digit is typed
      if (isDigitKey(e)) {
        e.preventDefault(); // prevent the browser from inserting it
        const digit = key; // guaranteed 0-9 here
        setAt(i, digit); // update state even if it’s the same
        hapticsImpactLight();
        if (i === length - 1) {
          inputs.current[i]?.blur();
        } else {
          focusNext(i);
        }
        return;
      }

      // 2) navigation / edits
      if (key === 'Backspace') {
        if (values[i]) {
          setAt(i, '');
        } else if (i > 0) {
          setAt(i - 1, '');
          focusPrev(i);
        }
        e.preventDefault();
        return;
      }

      if (key === 'Delete') {
        setAt(i, '');
        e.preventDefault();
        return;
      }

      if (key === 'ArrowLeft') {
        focusPrev(i);
        e.preventDefault();
        return;
      }
      if (key === 'ArrowRight') {
        focusNext(i);
        e.preventDefault();
        return;
      }

      if (key === ' ' || key === 'Spacebar' || key === 'Space') {
        e.preventDefault();
      }
    };

    const handlePaste = (
      i: number,
      e: React.ClipboardEvent<HTMLInputElement>,
    ) => {
      if (isDisabled) return;
      const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '');
      if (!pasted) return;
      e.preventDefault();

      const slice = pasted.slice(0, length - i).split('');
      const next = values.slice();
      for (let j = 0; j < slice.length; j++) {
        next[i + j] = slice[j];
      }
      setValues(next);
      const lastIndex = Math.min(i + slice.length - 1, length - 1);
      if (lastIndex < length - 1) {
        inputs.current[lastIndex + 1]?.focus();
      } else {
        inputs.current[lastIndex]?.blur();
      }
    };

    // Autofocus first input once on mount (not when loading toggles)
    useEffect(() => {
      if (!autoFocus) return;
      inputs.current[0]?.focus();
      // only run on first mount
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <Wrapper
        className={className}
        role='group'
        aria-label={ariaLabel}
        aria-invalid={error || undefined}
        aria-busy={loading || undefined}
      >
        <Row $dimmed={isDisabled}>
          {values.map((v, i) => (
            <Digit
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              type='text'
              inputMode='numeric'
              autoComplete='one-time-code'
              pattern='[0-9]*'
              value={mask && v ? '•' : v}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={(e) => handlePaste(i, e)}
              onFocus={(e) => e.currentTarget.select()}
              aria-label={`Digit ${i + 1}`}
              name={`${name}-${i}`}
              disabled={isDisabled}
              $error={error}
              $size={size}
            />
          ))}
        </Row>

        {loading && (
          <Overlay>
            <Spinner />
          </Overlay>
        )}
      </Wrapper>
    );
  },
);

export default VerificationCodeInput;

/* ===================== styles ===================== */

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const Row = styled.div<{ $dimmed: boolean }>`
  display: flex;
  gap: 14px;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.6 : 1)};
  pointer-events: ${({ $dimmed }) => ($dimmed ? 'none' : 'auto')};
`;

const Digit = styled.input<{ $error?: boolean; $size: Size }>`
  width: 100%;
  max-width: 44px;
  height: 56px;
  font-size: 22px;
  border-radius: 14px;

  /* Visuals */
  text-align: center;
  font-weight: 700;
  line-height: 1;
  color: ${({ theme }) => theme.text.title};
  background: ${({ theme }) => theme.background.light};
  border: 1px solid transparent;
  outline: none;
  caret-color: ${({ theme }) => theme.text.title};
  transition:
    box-shadow 120ms ease,
    border-color 120ms ease,
    background 120ms ease;

  &:focus {
    background: ${({ theme }) => theme.background.primary};
    border-color: ${({ theme }) => theme.primary.accent};
    box-shadow: 0 0 0 3px rgba(47, 107, 255, 0.18);
  }

  &:disabled {
    opacity: 1; /* keep legible; container dims */
    pointer-events: none;
  }

  ${({ $error }) =>
    $error &&
    `
      border-color: ${({ theme }) => theme.status.error.primary};
      box-shadow: 0 0 0 3px rgba(255, 59, 48, 0.18);
      color: ${({ theme }) => theme.status.error.primary};
    `}

  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  gap: 8px;
  pointer-events: none; /* clicks already blocked by Row dimming */
`;

const Spinner = styled.div`
  width: 22px;
  height: 22px;
  border: 3px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  color: ${({ theme }) => theme.primary.accent};
`;
